import { Folder, LayoutGrid } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RuleDrive, RuleTreeNode } from '../api-types';
import type { AreaRules } from './rules-model';
import { useTargetClass } from './target';

function RuleCard({
  id,
  title,
  intro,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly intro?: string;
  readonly children: ReactNode;
}) {
  const marked = useTargetClass(id);
  return (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className={`bg-card border-border scroll-mt-4 rounded-md border p-4 ${marked ?? ''}`}
    >
      <h3 id={`${id}-title`} className="text-lg font-bold">
        {title}
      </h3>
      {intro ? <p className="text-muted-foreground mt-1 text-sm">{intro}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function TreeNode({ node }: { readonly node: RuleTreeNode }) {
  const marked = useTargetClass(node.section);
  return (
    <li id={node.section} className={`scroll-mt-4 ${marked ?? ''}`}>
      <p className="flex items-center gap-2 font-semibold">
        <Folder aria-hidden="true" className="size-4 shrink-0" />
        {node.name}
      </p>
      {node.notes ? <p className="text-muted-foreground ml-6 text-xs">{node.notes}</p> : null}
      {node.children && node.children.length > 0 ? (
        <ul className="border-border m-0 ml-2 flex list-none flex-col gap-2 border-l pt-2 pl-4">
          {node.children.map((child) => (
            <TreeNode key={child.section} node={child} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function DriveCard({ drive }: { readonly drive: RuleDrive }) {
  const { t } = useTranslation('archive');
  return (
    <RuleCard
      id={drive.section}
      title={t('rules.driveTitle', { code: drive.code, name: drive.name })}
      intro={t('rules.drivePrefix', { code: drive.code, name: drive.name })}
    >
      <p className="mb-2 flex items-center gap-2 font-bold">
        <LayoutGrid aria-hidden="true" className="size-4" />
        {drive.code} {drive.name}
      </p>
      <ul className="border-border m-0 ml-2 flex list-none flex-col gap-3 border-l pl-4">
        {drive.tree.map((node) => (
          <TreeNode key={node.section} node={node} />
        ))}
      </ul>
    </RuleCard>
  );
}

function Entry({ id, children }: { readonly id: string; readonly children: ReactNode }) {
  const marked = useTargetClass(id);
  return (
    <li id={id} className={`scroll-mt-4 py-1 ${marked ?? ''}`}>
      {children}
    </li>
  );
}

const LIST = 'm-0 flex list-none flex-col gap-1 p-0';

/** The sections of one area, each with the anchor id the API gives it. */
export function RuleSections({ rules }: { readonly rules: AreaRules }) {
  const { t } = useTranslation('archive');
  return (
    <div className="flex flex-col gap-4">
      {rules.drives.map((drive) => (
        <DriveCard key={drive.section} drive={drive} />
      ))}
      {rules.naming ? (
        <RuleCard
          id={rules.naming.section}
          title={t('rules.naming')}
          intro={t('rules.namingPattern')}
        >
          <p className="bg-muted rounded-md p-2 font-mono text-sm break-words">
            {rules.naming.pattern}
          </p>
          <ul className={`${LIST} mt-3`}>
            {rules.naming.parts.map((part) => (
              <Entry key={part.section} id={part.section}>
                {part.label}
              </Entry>
            ))}
          </ul>
        </RuleCard>
      ) : null}
      {rules.types.length > 0 ? (
        <RuleCard id="types" title={t('rules.types')}>
          <ul className={LIST}>
            {rules.types.map((type) => (
              <Entry key={type.section} id={type.section}>
                <strong>{type.code}</strong> · {type.label}
              </Entry>
            ))}
          </ul>
        </RuleCard>
      ) : null}
      {rules.codes.length > 0 ? (
        <RuleCard id="codes" title={t('rules.codes')}>
          <ul className={LIST}>
            {rules.codes.map((code) => (
              <Entry key={code.section} id={code.section}>
                <strong>{code.code}</strong> · {code.label} ·{' '}
                <span className="bg-muted rounded-full px-2 py-0.5 text-xs font-semibold">
                  {t(`rules.codeState.${code.state}`, { defaultValue: code.state })}
                </span>
                {code.aliases.length > 0 ? (
                  <span className="text-muted-foreground block text-xs">
                    {t('rules.alias', { aliases: code.aliases.join(', ') })}
                  </span>
                ) : null}
              </Entry>
            ))}
          </ul>
        </RuleCard>
      ) : null}
      {rules.conventions.length > 0 ? (
        <RuleCard id="conventions" title={t('rules.conventions')}>
          <ul className={LIST}>
            {rules.conventions.map((convention) => (
              <Entry key={convention.section} id={convention.section}>
                {convention.text}
              </Entry>
            ))}
          </ul>
        </RuleCard>
      ) : null}
    </div>
  );
}
